import { DriverSchedule, PointsOfInterestResponse } from './../../../../OrderDispatch/DriverTracking/types'
import { isDefined } from '@/utils/general'
import type { Invoice, InvoiceDetail, InvoiceTransaction } from '../../../../../api/useFreight/types'
import { DriverTrackingResponse } from '@/components/OrderDispatch/DriverTracking/types'
import {
  createRoutes,
  getActionPinsGeoJSON,
  getPointsOfInterestOnRoute,
  getRouteProperties,
} from '@/components/OrderDispatch/DriverTracking/components/DriverMap/util'
import { DriverTrackingState } from '@/components/OrderDispatch/DriverTracking/stores'
import { DEFAULT_VIEW_STATE } from '@/components/OrderDispatch/DriverTracking/components/DriverMap/constants'
import { LngLatBoundsLike, LngLatLike } from 'mapbox-gl'
import { getMapBoundsFromCoordinates } from '@/components/OrderDispatch/DriverTracking/util'

export interface CalculatedAmounts {
  baseAmount: number
  accessorialAmount: number
  surchargeAmount: number
  totalAmount: number
}

/**
 * Calculates the totals for different transaction types from an invoice
 *
 * This function categorizes amounts into base freight, accessorials, and surcharges
 * using either the credit_rebill_metadata.impact_type or falling back to the detail.type
 *
 * @param transactions - The invoice transactions to process
 * @returns Object containing categorized totals
 */
export function calculateTransactionAmounts(invoice: Invoice): CalculatedAmounts {
  // Use reduce instead of forEach for a more functional approach
  return invoice.transactions.reduce(
    (amounts: CalculatedAmounts, transaction: InvoiceTransaction) => {
      // Process each detail in the transaction
      const detailAmounts = transaction.details.reduce(
        (detailAccumulator, detail) => {
          // Use override values if available, otherwise use original
          const detailTotal =
            detail.total_override !== null && detail.total_override !== undefined ? detail.total_override : detail.total

          // Update total amount
          detailAccumulator.totalAmount += detailTotal

          // First check impact_type from credit_rebill_metadata which is more reliable for categorization
          if (detail.credit_rebill_metadata?.impact_type === 'base') {
            detailAccumulator.baseAmount += detailTotal
          } else if (detail.credit_rebill_metadata?.impact_type === 'accessorial') {
            detailAccumulator.accessorialAmount += detailTotal
          } else if (detail.credit_rebill_metadata?.impact_type === 'surcharge') {
            detailAccumulator.surchargeAmount += detailTotal
          } else {
            // Fallback to type-based categorization if impact_type is not available
            if (detail.type === 'Base Freight' || detail.type === 'Point to Point') {
              detailAccumulator.baseAmount += detailTotal
            } else if (detail.type === 'Accessorial') {
              detailAccumulator.accessorialAmount += detailTotal
            } else if (detail.type === 'Surcharge') {
              detailAccumulator.surchargeAmount += detailTotal
            }
          }

          return detailAccumulator
        },
        {
          baseAmount: amounts.baseAmount,
          accessorialAmount: amounts.accessorialAmount,
          surchargeAmount: amounts.surchargeAmount,
          totalAmount: amounts.totalAmount,
        }
      )

      return detailAmounts
    },
    {
      baseAmount: 0,
      accessorialAmount: 0,
      surchargeAmount: 0,
      totalAmount: 0,
    }
  )
}

/**
 * Calculates the total miles driven from an invoice's transactions
 *
 * @param transactions - The invoice transactions to process
 * @returns Total miles driven
 */
export function calculateTotalMiles(invoice: Invoice): number {
  return invoice.transactions.reduce((total, transaction) => total + transaction.distance, 0)
}

export function getOverrideValue(newValue: string | number | null, invoiceDetail: InvoiceDetail, key: string) {
  const initialValue = invoiceDetail[key as keyof InvoiceDetail] as string | number | null
  const overrideValue = invoiceDetail[`${key}_override` as keyof InvoiceDetail] as string | number | null
  const originalValue = invoiceDetail[`${key}_original` as keyof InvoiceDetail] as string | number | null

  if (isDefined(overrideValue) && newValue === overrideValue) {
    return overrideValue
  }

  if ((!isDefined(overrideValue) && newValue === initialValue) || newValue === originalValue) {
    return null
  }
  return newValue
}

export function payloadHasOverrides(invoiceDetail: InvoiceDetail): boolean {
  return Object.keys(invoiceDetail).some((key) => {
    if (key === 'total_override') {
      if (invoiceDetail[key as keyof InvoiceDetail] === null) return false
      // If the delta is effectively zero (accounting for floating point precision)
      // This handles the case where a value was changed and changed back
      const originalTotal = invoiceDetail.total_original
      const currentTotal = invoiceDetail.total
      return Math.abs(originalTotal - currentTotal) > 0.01
    }
    return key.endsWith('_override') && key !== 'has_override' && invoiceDetail[key as keyof InvoiceDetail] !== null
  })
}

export function setRemovedTotalsToZero(invoice: Invoice): Invoice {
  const updatedInvoice = invoice.transactions.map((transaction) => {
    const updatedDetails = transaction.details.map((detail) => {
      if (detail.credit_rebill_metadata?.impact_type === 'removed') {
        return {
          ...detail,
          total: 0,
          total_override: 0,
        }
      }
      return detail
    })

    return {
      ...transaction,
      details: updatedDetails,
    }
  })
  return {
    ...invoice,
    transactions: updatedInvoice,
  }
}

export function getInvoiceBreadcrumbRoute(driverSearchResult: DriverTrackingResponse | undefined, orderNumber: number) {
  const breadcrumbs = driverSearchResult?.flatMap((driver) =>
    driver.breadcrumbs.filter((b) => b.order_number === orderNumber)
  )
  const p = getRouteProperties(0)
  const routeProperties: DriverTrackingState['driverOrdersOnRoute'] = {
    [orderNumber]: {
      color: p.color,
      arrow: p.arrow,
      isVisible: true,
    },
  }
  const route = createRoutes(breadcrumbs, routeProperties, driverSearchResult?.[0]?.timezone)
  return route[orderNumber]
}

export function getInvoiceActionPins(driverSearchResult: DriverTrackingResponse | undefined, orderNumber: number) {
  const routeSteps = driverSearchResult?.flatMap((driver) => driver.route.filter((r) => r.order_number === orderNumber))
  const pins = getActionPinsGeoJSON(routeSteps, driverSearchResult?.[0]?.timezone)
  return pins[orderNumber]
}

export function getInvoicePointsOfInterest(
  driverSearchResult: DriverTrackingResponse | undefined,
  orderNumber: number,
  pointsOfInterest: PointsOfInterestResponse
) {
  const orderSteps =
    driverSearchResult?.map((d) => ({
      ...d,
      route: d.route.filter((r) => r.order_number === orderNumber),
    })) || []
  return getPointsOfInterestOnRoute(null, null, orderSteps, pointsOfInterest)
}

export function getInitialInvoiceBounds(driverSearchResult: DriverTrackingResponse | undefined, orderNumber: number) {
  const breadcrumbCoords = driverSearchResult
    ?.flatMap((driver) => driver.breadcrumbs.filter((b) => b.order_number === orderNumber))
    .map((b) => ({ lat: b.lat, lon: b.lon }))
  const routeActionCoords = driverSearchResult?.flatMap((driver) =>
    driver.route
      .filter((r) => r.order_number === orderNumber && (r.action === 'loading' || r.action === 'dropping'))
      .map((r) => ({ lat: r.manual_start_gps_lat, lon: r.manual_start_gps_lon }))
  )
  const coordinates = [...(breadcrumbCoords || []), ...(routeActionCoords || [])].filter(
    (c): c is { lat: number; lon: number } => c?.lat != null && c?.lon != null
  )
  return getMapBoundsFromCoordinates(coordinates)
}
